import { useEffect, useRef } from 'react';

interface UseDynamicScrollOptions {
  minHeight: number;
  enabled?: boolean;
}

/**
 * 화면 높이가 최소 높이보다 작을 때 body overflow를 auto로 변경하는 훅
 * @param minHeight 최소 높이 (px)
 * @param enabled 훅 활성화 여부
 */
export const useDynamicScroll = ({ minHeight, enabled = true }: UseDynamicScrollOptions) => {
  const originalOverflow = useRef<string>('');
  const isActive = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) return;

    // 원본 overflow 값 저장 (한 번만)
    if (!originalOverflow.current) {
      originalOverflow.current = document.body.style.overflow || 'hidden';
    }

    const checkHeight = () => {
      const viewportHeight = window.innerHeight;
      
      if (viewportHeight < minHeight) {
        // 화면이 최소 높이보다 작으면 스크롤 허용
        document.body.style.overflow = 'auto';
        isActive.current = true;
      } else {
        // 화면이 충분히 크면 원래대로 복원
        document.body.style.overflow = originalOverflow.current;
        isActive.current = false;
      }
    };

    // 초기 체크
    checkHeight();

    // 리사이즈 이벤트 리스너 등록
    window.addEventListener('resize', checkHeight);

    // cleanup 함수
    return () => {
      window.removeEventListener('resize', checkHeight);
      // 현재 활성화된 상태에서만 원래 상태로 복원
      if (isActive.current) {
        document.body.style.overflow = originalOverflow.current;
        isActive.current = false;
      }
    };
  }, [minHeight, enabled]);

  return {
    // 현재 body overflow 상태를 확인할 수 있는 함수
    getCurrentOverflow: () => document.body.style.overflow,
    // 수동으로 스크롤 상태를 토글할 수 있는 함수
    toggleScroll: (force?: boolean) => {
      if (force !== undefined) {
        document.body.style.overflow = force ? 'auto' : originalOverflow.current;
      } else {
        const current = document.body.style.overflow;
        document.body.style.overflow = current === 'auto' ? originalOverflow.current : 'auto';
      }
    }
  };
};
